/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { ensureCurrentUser } from '../../../../util/data';
import { isMobileDevice, isIOS } from '../../../../util/userAgent';

import {
  AvatarLarge,
  Icons,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
  IOSInstallInstructions,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import logo from '../../../../assets/onbuoy-logo-icon.png';
import Drawer from 'react-modern-drawer';
import '../../../../styles/reactModernDrawer.css';
import '../../../../styles/drawer.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    customLinks,
    onLogout,
    hasCurrentUserPhoneNumber,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const deferredPrompt = useRef(null);
  const isMobile = isMobileDevice();
  const isIOSDevice = isIOS();
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isDrawerOpen, setOpenDrawer] = useState(false);

  const extraLinks = customLinks.map(linkConfig => {
    return (
      <CustomLinkComponent
        key={linkConfig.text}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const MobileDrawer = (
    <Drawer
      open={isDrawerOpen}
      onClose={() => {
        setOpenDrawer(false);
      }}
      direction="bottom"
      className={css.filterDrawer}
      lockBackgroundScroll
      overlayOpacity="0.45"
      overlayColor="#000000"
    >
      <div className={css.drawerHead}>
        Intall the app{' '}
        <span
          className={css.close}
          onClick={() => {
            setOpenDrawer(false);
          }}
        >
          <Icons name="close" />
        </span>
      </div>

      <div className={css.drawerBody}>
        <div className={css.onbuoySec}>
          <div className={css.onbuoyLogo}>
            <img src={logo} alt="" />
          </div>
          <div className={css.onbuoyInfo}>
            <h2>OnBuoy</h2>
            <p>www.onbuoy.com</p>
          </div>
        </div>
        <div className={css.otherInfo}>
          <div className={css.info}>
            1. Tap on the
            <span className={css.upload}>
              <Icons name="uploadSquare" />
            </span>
            in the browser toolbar
          </div>
          <div className={css.info}>
            2. Scroll down and select <span className={css.highlight}>Add to Home Screen</span>
          </div>
          <div className={css.info}>
            3. Look for the
            <span className={css.iconlogo}>
              <img src={logo} alt="" />
            </span>
            icon on your home screen
          </div>
        </div>
      </div>
    </Drawer>
  );

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <h1>
            {' '}
            <FormattedMessage id="TopbarMobileMenu.mobileMenuHeader" />
          </h1>
          <ul>
            <li>
              <Icons name="checkCircle" />
              <FormattedMessage id="TopbarMobileMenu.mobileMenuLink1" />
            </li>
            <li>
              <Icons name="checkCircle" />
              <FormattedMessage id="TopbarMobileMenu.mobileMenuLink2" />
            </li>
            <li>
              <Icons name="checkCircle" />
              <FormattedMessage id="TopbarMobileMenu.mobileMenuLink3" />
            </li>
          </ul>
          <div className={css.authBtns}>
            {login} {signup}
          </div>
          {/* <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div> */}

          <div className={css.customLinksWrapper}>{extraLinks}</div>

          <div className={css.spacer} />
        </div>
        <div className={css.footer}>
          {isMobile ? (
            <button
              className={css.addToHomeScreenButton}
              onClick={() => {
                setOpenDrawer(true);
              }}
            >
              <Icons name="downloadApp" />
              <FormattedMessage id="TopbarMobileMenu.addToHomeScreen" />
            </button>
          ) : (
            <NamedLink className={css.createNewListingLink} name="NewListingPage">
              <FormattedMessage id="TopbarMobileMenu.newListingLink" />
            </NamedLink>
          )}
        </div>
        <IOSInstallInstructions
          isVisible={showIOSInstructions}
          onClose={() => setShowIOSInstructions(false)}
        />
        {MobileDrawer}
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('LandingPage'))}
          name="LandingPage"
        >
          Home
        </NamedLink>
        <NamedLink
          className={classNames(css.inbox, currentPageClass('InboxPage'))}
          name="InboxPage"
          params={{ tab: 'orders' }}
        >
          <FormattedMessage id="TopbarMobileMenu.inboxLink" />
          {notificationCountBadge}
        </NamedLink>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
          name="ManageListingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
        </NamedLink>{' '}
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('FavouritePage'))}
          name="FavouritePage"
        >
          <FormattedMessage id="UserNav.FavouritePage" />
        </NamedLink>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('MyBoatPage'))}
          name="MyBoatPage"
        >
          <FormattedMessage id="UserNav.myBoatPage" />
        </NamedLink>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
          name="ProfileSettingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
        </NamedLink>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
          name="AccountSettingsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
        </NamedLink>
        {/* <NamedLink
          className={classNames(css.navigationLink, currentPageClass('TermsOfServicePage'))}
          name="TermsOfServicePage"
        >
          <FormattedMessage id="FooterContainer.privacyPolicy" />
        </NamedLink>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('PrivacyPolicyPage'))}
          name="PrivacyPolicyPage"
        >
          <FormattedMessage id="FooterContainer.termsOfUse" />
        </NamedLink> */}
        <a className={css.navigationLink} target="_blank" href="https://www.onbuoy.com/help">
          Help & FAQs
        </a>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>
        {isMobile ? (
          <button
            className={css.addToHomeScreenButton}
            onClick={() => {
              setOpenDrawer(true);
            }}
          >
            {/* <span className="material-symbols-outlined">install_mobile</span> */}
            <Icons name="downloadApp" />
            <FormattedMessage id="TopbarMobileMenu.addToHomeScreen" />
          </button>
        ) : (
          <NamedLink
            className={css.createNewListingLink}
            name={hasCurrentUserPhoneNumber ? 'NewListingPage' : 'ContactDetailsPage'}
            to={!hasCurrentUserPhoneNumber ? { state: { phoneNumberRequiredBanner: true } } : null}
          >
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        )}
      </div>
      <IOSInstallInstructions
        isVisible={showIOSInstructions}
        onClose={() => setShowIOSInstructions(false)}
      />
      {MobileDrawer}
    </div>
  );
};

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
